import React, { useEffect, useState } from 'react';
import { odAPI } from '../services/api';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { toast } from 'sonner';
import { Briefcase, Plus, Loader2, X } from 'lucide-react';

const STATUS_VARIANT = {
  pending_manager: 'warning', manager_approved: 'default', approved: 'success', rejected: 'destructive', cancelled: 'secondary',
};
const OD_TYPES = [
  { v: 'client_visit', l: 'Client visit' },
  { v: 'training', l: 'Training' },
  { v: 'off_site', l: 'Off-site' },
  { v: 'conference', l: 'Conference' },
  { v: 'field', l: 'Field work' },
  { v: 'other', l: 'Other' },
];

const MyOD = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const today = new Date().toISOString().split('T')[0];
  const [form, setForm] = useState({ start_date: today, end_date: today, od_type: 'client_visit', purpose: '', location: '' });

  const fetchData = async () => {
    setLoading(true);
    try { const r = await odAPI.mine(); setRows(r.data || []); }
    catch { toast.error('Failed to load'); }
    finally { setLoading(false); }
  };
  useEffect(() => { fetchData(); }, []);

  const submit = async () => {
    if (!form.purpose.trim()) { toast.error('Purpose is required'); return; }
    setBusy(true);
    try {
      await odAPI.create(form);
      toast.success('OD request submitted');
      setOpen(false);
      setForm({ start_date: today, end_date: today, od_type: 'client_visit', purpose: '', location: '' });
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    finally { setBusy(false); }
  };

  const cancel = async (id) => {
    if (!window.confirm('Cancel this OD request?')) return;
    try { await odAPI.cancel(id); toast.success('Cancelled'); fetchData(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2"><Briefcase size={22} />On Duty (OD)</h1>
          <p className="text-muted-foreground text-sm">Request outdoor / official duty. Approved days are auto-marked as Present-OD on your attendance.</p>
        </div>
        <Button onClick={() => setOpen(true)} data-testid="myod-new-btn"><Plus size={14} className="mr-1" />Request OD</Button>
      </div>

      {loading ? (
        <div className="text-sm text-muted-foreground flex items-center gap-2"><Loader2 size={14} className="animate-spin" />Loading…</div>
      ) : rows.length === 0 ? (
        <Card><CardContent className="p-8 text-center text-sm text-muted-foreground">No OD requests yet.</CardContent></Card>
      ) : (
        <div className="space-y-2" data-testid="myod-list">
          {rows.map((w) => (
            <Card key={w.id} data-testid={`myod-row-${w.id}`}>
              <CardContent className="p-4 flex items-center gap-3 flex-wrap">
                <Briefcase size={16} className="text-muted-foreground" />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-medium">{w.start_date} → {w.end_date}</span>
                    <Badge variant={STATUS_VARIANT[w.status] || 'secondary'}>{w.status.replace('_', ' ')}</Badge>
                    <Badge variant="outline" className="text-xs">{(OD_TYPES.find((t) => t.v === w.od_type) || {}).l || w.od_type}</Badge>
                    <span className="text-xs text-muted-foreground">{w.days} day(s)</span>
                  </div>
                  <div className="text-xs text-muted-foreground mt-1 truncate">{w.purpose} {w.location ? `· ${w.location}` : ''}</div>
                  {w.rejection_note && <div className="text-xs text-red-600 mt-1">Rejected: {w.rejection_note}</div>}
                </div>
                {['pending_manager', 'manager_approved', 'approved'].includes(w.status) && (
                  <Button size="sm" variant="ghost" onClick={() => cancel(w.id)} data-testid={`myod-cancel-${w.id}`}><X size={14} className="text-red-600" /></Button>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg" data-testid="myod-dialog">
          <DialogHeader><DialogTitle>Request OD</DialogTitle><DialogDescription>Your reporting manager approves first, then HR.</DialogDescription></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Start date</Label><Input type="date" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} data-testid="myod-start" /></div>
            <div><Label>End date</Label><Input type="date" value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} data-testid="myod-end" /></div>
            <div><Label>Type</Label>
              <Select value={form.od_type} onValueChange={(v) => setForm({ ...form, od_type: v })}>
                <SelectTrigger data-testid="myod-type"><SelectValue /></SelectTrigger>
                <SelectContent>{OD_TYPES.map((t) => <SelectItem key={t.v} value={t.v}>{t.l}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Location</Label><Input value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} placeholder="City / venue" data-testid="myod-location" /></div>
            <div className="col-span-2"><Label>Purpose</Label><Input value={form.purpose} onChange={(e) => setForm({ ...form, purpose: e.target.value })} placeholder="Reason / client / project" data-testid="myod-purpose" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={submit} disabled={busy} data-testid="myod-submit">{busy ? <Loader2 size={14} className="animate-spin mr-2" /> : null}Submit</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default MyOD;
